// frontend/src/components/ExcludedStaffPanel.jsx
// Supabase対応版

import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabase';
import { useOrganization } from '../contexts/OrganizationContext';

// 入職年度 → 経験年数（4月始まり）
const yearToExperience = (year) => {
  if (!year) return null;
  const now = new Date();
  const currentFiscalYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return currentFiscalYear - Number(year) + 1;
};

const ExcludedStaffPanel = ({ selectedDate }) => {
  const { currentOrganization } = useOrganization();
  const [allStaff, setAllStaff] = useState([]);
  const [isSettingMode, setIsSettingMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchYear, setSearchYear] = useState('');

  // スタッフ一覧取得
  useEffect(() => {
    if (!currentOrganization?.id) return;
    const fetchStaff = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('staff')
          .select('id, last_name, first_name, employee_id, is_excluded, year')
          .eq('organization_id', currentOrganization.id)
          .eq('is_active', true)
          .order('last_name');
        if (error) throw error;
        setAllStaff(data || []);
      } catch (e) {
        console.error('スタッフ取得エラー:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchStaff();
  }, [currentOrganization?.id]);

  const excludedStaff = allStaff.filter(s => s.is_excluded);

  // 利用可能な学年リスト
  const availableYears = useMemo(() => {
    const years = [...new Set(allStaff.map(s => s.year).filter(Boolean))];
    return years.sort((a, b) => Number(b) - Number(a)); // 新しい入職年度が1年目なので降順
  }, [allStaff]);

  // フィルタリング済みスタッフ
  const filteredStaff = useMemo(() => {
    return allStaff.filter(s => {
      const fullName = `${s.last_name || ''}${s.first_name || ''}`;
      const matchName = searchText === '' || fullName.includes(searchText);
      const matchYear = searchYear === '' || String(s.year) === searchYear;
      return matchName && matchYear;
    });
  }, [allStaff, searchText, searchYear]);

  const toggleExclude = async (staff) => {
    const newValue = !staff.is_excluded;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('staff')
        .update({ is_excluded: newValue })
        .eq('id', staff.id);
      if (error) throw error;
      setAllStaff(prev =>
        prev.map(s => s.id === staff.id ? { ...s, is_excluded: newValue } : s)
      );
    } catch (e) {
      console.error('対象外設定エラー:', e);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="border rounded-lg p-4 mb-4 bg-white shadow-sm">
        <div className="text-gray-400 text-sm text-center py-2">読み込み中...</div>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-4 mb-4 bg-white shadow-sm">
      <div className="flex justify-between items-center mb-3">
        <span className="text-base font-bold text-gray-800">👥 配置対象外スタッフ</span>
        <button
          onClick={() => { setIsSettingMode(v => !v); setSearchText(''); setSearchYear(''); }}
          className={`px-3 py-1.5 text-sm rounded border font-medium transition-colors ${isSettingMode ? 'bg-gray-200 border-gray-400 text-gray-700' : 'bg-gray-100 border-gray-300 text-gray-600 hover:bg-gray-200'}`}
        >
          {isSettingMode ? '✕ 閉じる' : '⚙️ 設定'}
        </button>
      </div>

      {isSettingMode ? (
        <div>
          <p className="text-xs text-gray-500 mb-3">チェックしたスタッフは人員配置モードに表示されません（永続設定）</p>

          {/* 検索フィルター */}
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder="名前で検索..."
              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-300"
            />
            <select
              value={searchYear}
              onChange={e => setSearchYear(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white"
            >
              <option value="">全学年</option>
              {availableYears.map(y => (
                <option key={y} value={String(y)}>{yearToExperience(y)}年目</option>
              ))}
            </select>
            {(searchText || searchYear) && (
              <button
                onClick={() => { setSearchText(''); setSearchYear(''); }}
                className="px-2 py-1.5 text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded-md"
              >
                ✕
              </button>
            )}
          </div>

          {/* 件数表示 */}
          <div className="text-xs text-gray-400 mb-2">
            {filteredStaff.length}名表示中（全{allStaff.length}名）
            {excludedStaff.length > 0 && <span className="ml-2 text-orange-500">対象外: {excludedStaff.length}名</span>}
          </div>

          {/* スタッフ一覧 */}
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-52 overflow-y-auto p-2 bg-gray-50 rounded border">
            {filteredStaff.length === 0 ? (
              <div className="col-span-4 text-center text-gray-400 text-sm py-4">該当するスタッフがいません</div>
            ) : (
              filteredStaff.map(s => (
                <label
                  key={s.id}
                  className={`flex items-center gap-2 p-2 rounded cursor-pointer border text-sm transition-colors ${s.is_excluded ? 'bg-orange-100 border-orange-300 text-orange-700 font-medium' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                >
                  <input
                    type="checkbox"
                    checked={!!s.is_excluded}
                    onChange={() => toggleExclude(s)}
                    disabled={saving}
                    className="accent-orange-500 flex-shrink-0"
                  />
                  <div className="min-w-0">
                    <div className="truncate">{s.last_name} {s.first_name}</div>
                    {s.year && <div className="text-xs text-gray-400">{yearToExperience(s.year)}年目</div>}
                  </div>
                </label>
              ))
            )}
          </div>
          {saving && <p className="text-xs text-gray-400 mt-1">保存中...</p>}
        </div>
      ) : (
        // 表示モード
        <div>
          {excludedStaff.length === 0 ? (
            <div className="text-center text-gray-400 text-sm py-3">この日は全スタッフが配置対象です</div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {excludedStaff.map(s => (
                <span key={s.id} className="inline-flex items-center px-3 py-1 bg-orange-100 text-orange-700 border border-orange-300 rounded-full text-sm font-medium">
                  {s.last_name} {s.first_name}
                  {s.year && <span className="ml-1 text-orange-400 text-xs">{yearToExperience(s.year)}年目</span>}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExcludedStaffPanel;